<script>
  import { createEventDispatcher } from 'svelte';
  import { apiFetch } from '../lib/auth.js';

  // e.g. label="Event", endpoint="/events", searchParam="name", displayField="name"
  export let value = null;  
  export let label = 'Item';
  export let endpoint = '';  
  export let searchParam = 'name';
  export let displayField = 'name';
  export let subField = null; 
  export let placeholder = 'Search...';
  export let required = false;

  const dispatch = createEventDispatcher();

  let query = value ? value[displayField] : '';
  let results = [];
  let loading = false;
  let showDropdown = false;
  let debounceTimer;
  let searched = false; 

  async function search(q) {
    if (!q || q.length < 2) { results = []; showDropdown = false; searched = false; return; }
    loading = true;
    try {
      const data = await apiFetch(`${endpoint}?${searchParam}=${encodeURIComponent(q)}`);
      results = Array.isArray(data) ? data : (data.items || data[endpoint.replace('/', '')] || []);
      searched = true;
      showDropdown = true;
    } catch {
      results = [];
      searched = true;
      showDropdown = true;
    } finally {
      loading = false;
    }
  }

  function handleInput() {
    value = null;
    dispatch('change', null);
    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(() => search(query), 300);
  }

  function select(item) {
    value = item;
    query = item[displayField];
    showDropdown = false;
    searched = false;
    dispatch('change', item);
  }

  export function clear() {
    value = null;
    query = '';
    results = [];
    showDropdown = false;
    searched = false;
    dispatch('change', null);
  }

  function handleBlur() {
    setTimeout(() => { showDropdown = false; }, 160);
  }
</script>

<div class="search-field">
  <label>
    {label}
    {#if required}<span class="req">*</span>{/if}
  </label>
  <div class="input-wrap">
    <input
      type="text"
      bind:value={query}
      on:input={handleInput}
      on:blur={handleBlur}
      on:focus={() => searched && (showDropdown = true)}
      {placeholder}
      autocomplete="off"
    />
    {#if value}
      <span class="badge check">✓</span>
    {:else if loading}
      <span class="spinner"></span>
    {/if}
  </div>

  {#if showDropdown}
    <div class="dropdown">
      {#each results as item}
        <!-- svelte-ignore a11y-click-events-have-key-events -->
        <div class="dropdown-item" on:click={() => select(item)}>
          <span class="item-name">{item[displayField]}</span>
          {#if subField && item[subField]}<span class="item-meta">{item[subField]}</span>{/if}
        </div>
      {/each}

      {#if results.length === 0 && searched}
        <div class="dropdown-item no-results">No results for "{query}"</div>
      {/if}

      <!-- Optional "add new" slot — parent decides whether to show it -->
      <slot name="add-new" {query} />
    </div>
  {/if}
</div>

<style>
  @import url('https://fonts.googleapis.com/css2?family=Rajdhani:wght@400;600;700&display=swap');

  .search-field { display: flex; flex-direction: column; gap: 0.4rem; margin-bottom: 1rem; position: relative; }

  label {
    font-family: 'Rajdhani', sans-serif;
    font-weight: 600; font-size: 0.55rem;
    letter-spacing: 0.25em; text-transform: uppercase;
    color: #3a6b00;
  }

  .req { color: #cc4400; margin-left: 2px; }

  .input-wrap { position: relative; }

  input {
    background: #0a0a0a; border: 1px solid #1e3d00; border-radius: 2px;
    color: #c8c8c8; font-family: 'Rajdhani', sans-serif;
    font-size: 1rem; font-weight: 600;
    padding: 0.6rem 2rem 0.6rem 0.75rem;
    width: 100%; outline: none;
    transition: border-color 0.2s, box-shadow 0.2s;
  }
  input:focus { border-color: #3a6b00; box-shadow: 0 0 0 2px rgba(74,124,0,0.15); }

  .badge {
    position: absolute; right: 0.6rem; top: 50%;
    transform: translateY(-50%);
  }
  .check { color: #6db800; font-size: 0.85rem; font-weight: bold; }

  .spinner {
    position: absolute; right: 0.6rem; top: 50%;
    transform: translateY(-50%);
    width: 14px; height: 14px;
    border: 2px solid #1e3d00; border-top-color: #6db800;
    border-radius: 50%; animation: spin 0.7s linear infinite;
  }
  @keyframes spin { to { transform: translateY(-50%) rotate(360deg); } }

  .dropdown {
    position: absolute; top: 100%; left: 0; right: 0;
    background: #0f0f0f; border: 1px solid #2a5200;
    border-top: none; border-radius: 0 0 3px 3px;
    z-index: 300; box-shadow: 0 8px 24px rgba(0,0,0,0.8);
    max-height: 220px; overflow-y: auto;
  }

  .dropdown-item {
    display: flex; align-items: center; gap: 0.5rem;
    padding: 0.65rem 0.75rem; cursor: pointer;
    font-family: 'Rajdhani', sans-serif; font-size: 0.9rem; font-weight: 600;
    color: #c8c8c8; border-bottom: 1px solid #0f1a00;
    transition: background 0.15s;
  }
  .dropdown-item:hover { background: #0d1a00; }
  .dropdown-item:last-child { border-bottom: none; }

  .no-results { color: #2a5200; font-size: 0.75rem; font-style: italic; cursor: default; }
  .no-results:hover { background: none; }

  .item-name { flex: 1; }
  .item-meta { font-size: 0.65rem; letter-spacing: 0.1em; text-transform: uppercase; color: #3a6b00; }
</style>
